import React, { useState, useContext, useEffect } from 'react'
import { ButtonPrimary, ButtonSecondary } from '../../../../components/atoms'
import FormFieldText from '../../../../components/forms/FormFieldText'
import { Modal, ModalBody, ModalFooter } from '../../../../components/modal'
import { useTranslation } from 'react-i18next'
import { AppContext } from '../../../../global'
import * as actions from '../store/actions'
import { useValidation } from '../../../validation'
import BarWarning from '../../../../components/atoms/BarWarning'
import BarError from '../../../../components/atoms/BarError'
import { RequestState } from '../../../types'

type Props = {
  onClose: () => void
  onSave: () => void
  contactId: string | null
  accountId: string
}

function ContactForm(props: Props) {
  const { onClose, contactId, accountId, onSave } = props
  const { t } = useTranslation(['global', 'settings_contacts', 'form'])
  const { state, execute } = useContext(AppContext)
  const { fieldEmpty, emailError, phoneNumberError } = useValidation()
  const formSettingsState = state.settings.contacts.form
  const [warningMessageVisible, setWarningMessageVisible] = useState(false)

  useEffect(() => {
    if (formSettingsState.requestState === RequestState.Loaded) {
      onSave()
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formSettingsState.requestState])

  function updateAddFormfield(field: string, value: string) {
    execute(actions.updateAddFormField(field, value))
  }

  function someFieldsErrored() {
    const { name, email, phone, mobile } = formSettingsState.contact

    if (
      fieldEmpty(name) ||
      emailError(email, false) ||
      phoneNumberError(phone, false) ||
      phoneNumberError(mobile, false)
    ) {
      return true
    } else {
      return false
    }
  }

  function formHasWarning(): boolean {
    const { email, phone, mobile } = formSettingsState.contact

    if (fieldEmpty(email) && fieldEmpty(phone) && fieldEmpty(mobile)) {
      return true
    } else {
      return false
    }
  }

  async function onSaveLocal() {
    const warning = formHasWarning()
    setWarningMessageVisible(warning)

    if (!warning) {
      if (contactId === null) {
        await execute(actions.addContact(accountId, formSettingsState.contact))
      } else {
        await execute(actions.updateContact(accountId, contactId, formSettingsState.contact))
      }
    }
  }

  function updateFormFieldAndClearValidate(field: string, value: string) {
    setWarningMessageVisible(false)
    updateAddFormfield(field, value)
  }

  const saveButtonText =
    formSettingsState.requestState === RequestState.Loading
      ? t('global:saving')
      : t('global:save')

  const saveButtonDisabled =
    formSettingsState.requestState === RequestState.Loading ||
    someFieldsErrored()

  return (
    <Modal title={t('settings_contacts:contact_details')}>
      <ModalBody data-cy="contact-form" id="contact-form">
        <FormFieldText
          title={t('form:name')}
          value={formSettingsState.contact.name}
          error={fieldEmpty(formSettingsState.contact.name)}
          placeholder={t('form:enter_name')}
          data-cy="name"
          mandatory
          onChange={(value: string) => updateAddFormfield('name', value)} />

        <FormFieldText
          title={t('form:email')}
          value={formSettingsState.contact.email}
          error={emailError(formSettingsState.contact.email, false)}
          placeholder={t('form:enter_email')}
          data-cy="email"
          onChange={(value: string) => updateFormFieldAndClearValidate('email', value)} />

        <FormFieldText
          title={t('form:mobile')}
          value={formSettingsState.contact.mobile}
          placeholder={t('form:enter_mobile')}
          data-cy="mobile"
          error={phoneNumberError(formSettingsState.contact.mobile, false)}
          onChange={(value: string) => updateFormFieldAndClearValidate('mobile', value)} />

        <FormFieldText
          title={t('form:phone')}
          value={formSettingsState.contact.phone}
          error={phoneNumberError(formSettingsState.contact.phone, false)}
          placeholder={t('form:enter_phone')}
          data-cy="phone"
          onChange={(value: string) => updateFormFieldAndClearValidate('phone', value)} />

        {warningMessageVisible && (
          <BarWarning message={t('settings_contacts:form_warning')} />
        )}

        {formSettingsState.requestState === RequestState.Error && (
          <BarError message={t('settings_contacts:form_error')} />
        )}
      </ModalBody>
      <ModalFooter>
        <ButtonSecondary onClick={onClose}>{t('global:cancel')}</ButtonSecondary>
        <ButtonPrimary
          disabled={saveButtonDisabled}
          data-cy="btn-save-contact"
          onClick={onSaveLocal}>
          {saveButtonText}
        </ButtonPrimary>
      </ModalFooter>
    </Modal>
  )
}

export default ContactForm
